import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { hashPresentationPin, isPresentationPinHash, verifyPresentationPin } from '../utils/presentationPin';
import { persistPrivacyMode, readPersistedPrivacyMode } from '../utils/privacyMasking';

export const PRESENTATION_MODE_SESSION_KEY = 'pt.presentationMode';
let credentialRequestGeneration = 0;
let toggleRequestGeneration = 0;
let credentialMutationQueue: Promise<void> = Promise.resolve();

function queueCredentialMutation<T>(operation: () => Promise<T>): Promise<T> {
    const result = credentialMutationQueue.then(operation, operation);
    credentialMutationQueue = result.then(() => undefined, () => undefined);
    return result;
}

function readPresentationMode(): { active: boolean; storageAvailable: boolean } {
    try {
        return {
            // A link-driven privacy flag (pt.privacyMode) is the same switch: it also keeps this tab masked until the PIN is given.
            active: window.sessionStorage.getItem(PRESENTATION_MODE_SESSION_KEY) === '1' || readPersistedPrivacyMode(),
            storageAvailable: true,
        };
    } catch {
        // A storage failure cannot prove that the prior protected session was off.
        // Stay masked until the user explicitly verifies the PIN and turns it off.
        return { active: true, storageAvailable: false };
    }
}

function persistPresentationMode(active: boolean): boolean {
    try {
        if (active) window.sessionStorage.setItem(PRESENTATION_MODE_SESSION_KEY, '1');
        else window.sessionStorage.removeItem(PRESENTATION_MODE_SESSION_KEY);
        return true;
    } catch {
        // Presentation mode remains usable in memory when sessionStorage is unavailable.
        return false;
    }
}

const initialSessionState = readPresentationMode();

async function writePinCredential(userId: string, pinHash: string | null): Promise<boolean> {
    return queueCredentialMutation(async () => {
        const { error } = await supabase.rpc(
            'presentation_pin_credential_write_v1' as never,
            { p_expected_user_id: userId, p_pin_hash: pinHash } as never,
        );
        if (error) {
            console.error('Failed to update presentation PIN credential.');
            return false;
        }
        return true;
    });
}

async function upgradeLegacyPinCredential(userId: string, pinHash: string): Promise<boolean> {
    return queueCredentialMutation(async () => {
        const { data, error } = await supabase.rpc(
            'presentation_pin_credential_upgrade_v1' as never,
            { p_expected_user_id: userId, p_pin_hash: pinHash } as never,
        );
        if (error || data !== true) {
            console.error('Failed to upgrade presentation PIN credential.');
            return false;
        }
        return true;
    });
}

interface PresentationState {
    isPresentationMode: boolean;
    savedPin: string | null;
    pinLoaded: boolean;
    pinUserId: string | null;
    pinNeedsUpgrade: boolean;
    storageAvailable: boolean;
    loadPin: (userId: string) => Promise<void>;
    savePin: (userId: string, pin: string) => Promise<boolean>;
    removePin: (userId: string) => Promise<boolean>;
    recoverPin: (userId: string) => Promise<boolean>;
    verifyPin: (pin: string) => Promise<boolean>;
    /** A link or a wallboard asked for privacy mode (?privacy=on, display=overhead): turn it on. It can then be turned off only with the PIN (disablePresentationMode). */
    activateFromLink: () => void;
    /**
     * Undo an automatic activation (the AOC Situation room switches privacy on by itself when it opens).
     * The caller passes the values captured BEFORE that activation; the mode is put back to exactly that.
     * It is never a way to reveal data the person had masked: when the previous value was on, it stays on.
     */
    restoreSessionModes: (previous: { presentationMode: boolean; privacyMode: boolean }) => void;
    enablePresentationMode: (pin: string) => Promise<'success' | 'incorrect' | 'unavailable'>;
    disablePresentationMode: (pin: string) => Promise<'success' | 'incorrect' | 'unavailable'>;
}

export const usePresentationStore = create<PresentationState>()((set, get) => ({
    isPresentationMode: initialSessionState.active,
    savedPin: null,
    pinLoaded: false,
    pinUserId: null,
    pinNeedsUpgrade: false,
    storageAvailable: initialSessionState.storageAvailable,

    loadPin: async (userId: string) => {
        const generation = ++credentialRequestGeneration;
        // A refresh is an availability boundary even for the same user. Do not retain a
        // credential that the current read may prove stale or unavailable.
        set({ savedPin: null, pinLoaded: false, pinUserId: userId, pinNeedsUpgrade: false });
        try {
            // If a credential mutation was already dispatched, read only after it settles.
            // A mutation started later increments the generation and invalidates this read.
            await credentialMutationQueue;
            if (credentialRequestGeneration !== generation || get().pinUserId !== userId) return;
            const { data, error } = await supabase
                .from('user_profiles')
                .select('preferences')
                .eq('user_id', userId)
                .single();
            if (error) {
                console.error('Failed to load presentation PIN.');
                if (credentialRequestGeneration === generation && get().pinUserId === userId) {
                    set({ savedPin: null, pinLoaded: true, pinNeedsUpgrade: false });
                }
                return;
            }
            const preferences = (data?.preferences ?? {}) as Record<string, unknown>;
            const storedHash = preferences.presentation_pin_hash;
            const legacyPin = preferences.presentation_pin;

            if (isPresentationPinHash(storedHash)) {
                if (credentialRequestGeneration === generation && get().pinUserId === userId) {
                    set({ savedPin: storedHash, pinLoaded: true, pinUserId: userId, pinNeedsUpgrade: false });
                }
                return;
            }

            // A legacy plaintext value is converted to an in-memory hash immediately. Loading
            // remains read-only; the profile is upgraded only after the user explicitly enters
            // the correct PIN to toggle or change it.
            if (typeof legacyPin === 'string' && legacyPin.length > 0) {
                const migratedHash = await hashPresentationPin(legacyPin);
                if (credentialRequestGeneration === generation && get().pinUserId === userId) {
                    set({ savedPin: migratedHash, pinLoaded: true, pinUserId: userId, pinNeedsUpgrade: true });
                }
                return;
            }

            if (credentialRequestGeneration === generation && get().pinUserId === userId) {
                set({ savedPin: null, pinLoaded: true, pinUserId: userId, pinNeedsUpgrade: false });
            }
        } catch {
            console.error('Failed to load presentation PIN.');
            if (credentialRequestGeneration === generation && get().pinUserId === userId) {
                set({ savedPin: null, pinLoaded: true, pinUserId: userId, pinNeedsUpgrade: false });
            }
        }
    },

    savePin: async (userId: string, pin: string) => {
        const generation = ++credentialRequestGeneration;
        try {
            const pinHash = await hashPresentationPin(pin);
            if (credentialRequestGeneration !== generation || get().pinUserId !== userId) return false;
            if (!(await writePinCredential(userId, pinHash))) return false;
            if (credentialRequestGeneration !== generation || get().pinUserId !== userId) return false;
            set({ savedPin: pinHash, pinLoaded: true, pinUserId: userId, pinNeedsUpgrade: false });
            return true;
        } catch {
            console.error('Failed to save presentation PIN.');
            return false;
        }
    },

    removePin: async (userId: string) => {
        const generation = ++credentialRequestGeneration;
        try {
            if (get().pinUserId !== userId || !(await writePinCredential(userId, null))) return false;
            if (credentialRequestGeneration !== generation || get().pinUserId !== userId) return false;
            const storageAvailable = persistPresentationMode(false);
            persistPrivacyMode(false);
            set({ savedPin: null, isPresentationMode: false, pinLoaded: true, pinUserId: userId, pinNeedsUpgrade: false, storageAvailable });
            return true;
        } catch {
            console.error('Failed to remove presentation PIN.');
            return false;
        }
    },

    recoverPin: async (userId: string) => get().removePin(userId),

    verifyPin: async (pin: string) => {
        const savedPin = get().savedPin;
        return savedPin ? verifyPresentationPin(pin, savedPin) : false;
    },

    activateFromLink: () => {
        if (get().isPresentationMode) return;
        const storageAvailable = persistPresentationMode(true);
        set({ isPresentationMode: true, storageAvailable });
    },

    restoreSessionModes: (previous) => {
        const storageAvailable = persistPresentationMode(previous.presentationMode);
        persistPrivacyMode(previous.privacyMode);
        set({ isPresentationMode: previous.presentationMode || previous.privacyMode, storageAvailable: get().storageAvailable && storageAvailable });
    },

    enablePresentationMode: async (pin: string) => {
        const toggleGeneration = ++toggleRequestGeneration;
        const credentialGeneration = credentialRequestGeneration;
        const { pinUserId: userId, savedPin: credential, pinNeedsUpgrade } = get();
        if (!userId || !credential) return 'unavailable';
        if (!(await verifyPresentationPin(pin, credential))) return 'incorrect';
        if (toggleRequestGeneration !== toggleGeneration || credentialRequestGeneration !== credentialGeneration || get().pinUserId !== userId || get().savedPin !== credential) return 'unavailable';
        if (pinNeedsUpgrade) {
                if (!(await upgradeLegacyPinCredential(userId, credential))) return 'unavailable';
                if (toggleRequestGeneration !== toggleGeneration || credentialRequestGeneration !== credentialGeneration || get().pinUserId !== userId || get().savedPin !== credential || !get().pinNeedsUpgrade) return 'unavailable';
                set({ pinNeedsUpgrade: false });
        }
        const storageAvailable = persistPresentationMode(true);
        set({ isPresentationMode: true, storageAvailable });
        return 'success';
    },

    disablePresentationMode: async (pin: string) => {
        const toggleGeneration = ++toggleRequestGeneration;
        const credentialGeneration = credentialRequestGeneration;
        const { pinUserId: userId, savedPin: credential, pinNeedsUpgrade } = get();
        if (!userId || !credential) return 'unavailable';
        if (!(await verifyPresentationPin(pin, credential))) return 'incorrect';
        if (toggleRequestGeneration !== toggleGeneration || credentialRequestGeneration !== credentialGeneration || get().pinUserId !== userId || get().savedPin !== credential) return 'unavailable';
        if (pinNeedsUpgrade) {
                if (!(await upgradeLegacyPinCredential(userId, credential))) return 'unavailable';
                if (toggleRequestGeneration !== toggleGeneration || credentialRequestGeneration !== credentialGeneration || get().pinUserId !== userId || get().savedPin !== credential || !get().pinNeedsUpgrade) return 'unavailable';
                set({ pinNeedsUpgrade: false });
        }
        const storageAvailable = persistPresentationMode(false);
        // The link-driven flag (pt.privacyMode) is the same switch: only a correct PIN clears it.
        persistPrivacyMode(false);
        set({ isPresentationMode: false, storageAvailable });
        return 'success';
    },
}));
